import { useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldErrors, UseFormReturn, useForm } from "react-hook-form";
import { PERMISSION, VERIFY_SSO_CHANGES_ROUTE } from "Constants";
import { useAllowedTo, useCurrentOrganization, useDialog } from "Hooks";
import { ClaimSource } from "Types";
import {
  apiErrorToast,
  buildOidcAuthorizationUrl,
  buildOidcStateParam,
  getBaseRoute,
} from "Utils";
import {
  useOidcConfigQuery,
  useUpdateOidcConfigMutation,
} from "../services/sso.api";
import { OIDCFormData, UpdateOidcConfigResponse } from "../types";
import { OIDCFormSchema } from "../utils";

type UseOidcSSOConfigForm = {
  errors: FieldErrors<OIDCFormData>;
  onSubmit: () => void;
  methods: UseFormReturn<OIDCFormData>;
  claimSource: ClaimSource;
  isDirty: boolean;
  isConfirmSaveOpen: boolean;
  openConfirmSaveDialog: () => void;
  closeConfirmSaveDialog: () => void;
  handleUpdateSSOConfiguration: () => void;
  isLoading: boolean;
};

const useOidcSSOConfigForm = (
  sso_oidc_configuration_id?: string | null,
): UseOidcSSOConfigForm => {
  const [updateOidcConfig] = useUpdateOidcConfigMutation();

  // this additional permissions check helps skip api calls when someone tries to access a
  // page they don't have natural access to
  const isAdmin = useAllowedTo({
    permissions: [PERMISSION.SETUP_SSO],
  });

  const confirmSaveDialog = useDialog();

  const currentOrganization = useCurrentOrganization();

  const { data: oidcConfiguration, isLoading: isOidcConfigLoading } =
    useOidcConfigQuery(
      {
        configId: sso_oidc_configuration_id ?? "",
      },
      {
        skip: !currentOrganization || !sso_oidc_configuration_id || !isAdmin,
      },
    );

  const handleUpdateSSOConfiguration = async () => {
    try {
      if (currentOrganization) {
        // we have to cast here
        // form fields are giving us "" for empty fields
        // so we can use the form schema to cast the form data
        // to get the correct null values for nullable fields
        const formData = OIDCFormSchema.cast(getValues()) as OIDCFormData;

        if (formData) {
          await updateOidcConfig({
            oidcSsoConfiguration: {
              organization_id: currentOrganization.id,
              ...formData,
            },
          })
            .unwrap()
            .then((response: UpdateOidcConfigResponse) => {
              // this will test the config is working before being applied
              window.location.href = buildOidcAuthorizationUrl({
                authorizationUrl: formData.authorization_endpoint,
                clientId: formData.client_id,
                responseType: "code",
                scopes: formData.scopes,
                state: buildOidcStateParam({
                  state: response.state,
                  payload: response.configuration_id,
                }),
                nonce: response.nonce,
                redirectUri: `${getBaseRoute()}/${VERIFY_SSO_CHANGES_ROUTE}`,
              });
            });
        }
      }
    } catch (error) {
      apiErrorToast(error, "Could not update SSO configuration");
    }
  };

  const onSubmit = () => {
    confirmSaveDialog.open();
  };

  const defaultValues = useMemo((): OIDCFormData => {
    return {
      authorization_endpoint: oidcConfiguration?.authorization_endpoint ?? "",
      client_id: oidcConfiguration?.client_id ?? "",
      client_secret: oidcConfiguration?.client_secret ?? "",
      claim_source: oidcConfiguration?.claim_source ?? "id_token",
      client_type: oidcConfiguration?.client_type ?? "confidential",
      issuer: oidcConfiguration?.issuer ?? null,
      jwks_endpoint: oidcConfiguration?.jwks_endpoint ?? null,
      org_role_mappings: oidcConfiguration?.org_role_mappings ?? [
        {
          mapping: "",
          org_role: "OrganizationAdmin",
        },
      ],
      scopes: oidcConfiguration?.scopes ?? "",
      token_algorithm: oidcConfiguration?.token_algorithm ?? "RS256",
      token_endpoint: oidcConfiguration?.token_endpoint ?? "",
      token_audience: oidcConfiguration?.token_audience ?? null,
      user_email_mapping: oidcConfiguration?.user_email_mapping ?? "",
      user_first_name_mapping:
        oidcConfiguration?.user_first_name_mapping ?? null,
      user_groups_mapping: oidcConfiguration?.user_groups_mapping ?? "",
      user_info_endpoint: oidcConfiguration?.user_info_endpoint ?? null,
      user_last_name_mapping: oidcConfiguration?.user_last_name_mapping ?? null,
    };
  }, [
    oidcConfiguration?.authorization_endpoint,
    oidcConfiguration?.client_id,
    oidcConfiguration?.client_secret,
    oidcConfiguration?.claim_source,
    oidcConfiguration?.client_type,
    oidcConfiguration?.user_first_name_mapping,
    oidcConfiguration?.issuer,
    oidcConfiguration?.jwks_endpoint,
    oidcConfiguration?.org_role_mappings,
    oidcConfiguration?.scopes,
    oidcConfiguration?.token_algorithm,
    oidcConfiguration?.token_endpoint,
    oidcConfiguration?.token_audience,
    oidcConfiguration?.user_email_mapping,
    oidcConfiguration?.user_groups_mapping,
    oidcConfiguration?.user_info_endpoint,
    oidcConfiguration?.user_last_name_mapping,
  ]);

  const methods = useForm<OIDCFormData>({
    resolver: yupResolver(OIDCFormSchema),
    defaultValues,
  });

  const {
    formState: { errors, isDirty },
    reset,
    handleSubmit,
    watch,
    getValues,
    clearErrors,
  } = methods;

  const claimSource = watch("claim_source");

  // This will watch the form and reset validation errors when org role mappings change.
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name?.startsWith("org_role_mappings")) {
        clearErrors("org_role_mappings");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  // we need this because our form conditionally renders depending on the chosen protocol.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    methods,
    claimSource,
    isDirty: isDirty,
    isConfirmSaveOpen: confirmSaveDialog.isOpen,
    openConfirmSaveDialog: confirmSaveDialog.open,
    closeConfirmSaveDialog: confirmSaveDialog.close,
    handleUpdateSSOConfiguration,
    isLoading: isOidcConfigLoading,
  };
};

export default useOidcSSOConfigForm;
